import React from "react";
import { useApp } from "../../../state/Context";
import Card from "../ui/Card";

export default function HowItsDone() {
  const { setSelection } = useApp();

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Back */}
      <div className="flex items-center">
        <button
          type="button"
          onClick={() => setSelection((prev) => ({ ...prev, step: "select" }))}
          className="
            rounded-xl border border-neutral-200 bg-white px-3 py-2
            text-sm font-semibold text-neutral-800
            hover:bg-neutral-100
            dark:border-neutral-800 dark:bg-neutral-950
            dark:text-neutral-100 dark:hover:bg-neutral-900
          "
        >
          ← Back to selection
        </button>
      </div>

      <Card className="p-6">
        {/* Header */}
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">How it’s done</h1>
        </div>

        {/* Sections */}
        <div className="mt-6 space-y-4">
          {/* What the calculator is doing */}
          <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4 dark:border-neutral-800 dark:bg-neutral-900">
            <h2 className="text-lg font-semibold">What the calculator is doing</h2>
            <p className="mt-2 text-sm text-neutral-700 dark:text-neutral-200">
              Degree Classify UK applies your university’s ruleset (credit requirements, module
              weightings, level/year weightings, and classification boundaries) to produce an
              estimated final average and classification.
            </p>
            <p className="mt-2 text-sm text-neutral-700 dark:text-neutral-200">
              Each module contributes a <span className="font-semibold">grade</span> and a{" "}
              <span className="font-semibold">credit weighting</span>. Module results are aggregated
              within each level or year, then processed using the institution’s progression and
              award algorithm.
            </p>
          </div>

          {/* Where the rules come from */}
          <div className="rounded-lg border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-950">
            <h2 className="text-lg font-semibold">Where the rules come from</h2>
            <p className="mt-2 text-sm text-neutral-700 dark:text-neutral-200">
              Supported universities use institution-specific calculation rules sourced from
              official academic regulations and Freedom of Information (FOI) requests relating to
              degree classification.
            </p>
            <p className="mt-2 text-sm text-neutral-700 dark:text-neutral-200">
              Rulesets vary by degree type (Bachelors, Masters, and where available, higher awards)
              and define credit requirements, weightings, and classification thresholds.
            </p>
          </div>

          {/* Inputs and validation */}
          <div className="rounded-lg border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-950">
            <h2 className="text-lg font-semibold">Inputs and validation</h2>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-neutral-700 dark:text-neutral-200">
              <li>
                <span className="font-semibold">Credits</span> weight a module’s contribution within a
                level or year. On import, credits are clamped to a minimum of{" "}
                <span className="font-semibold">1</span>.
              </li>
              <li>
                <span className="font-semibold">Grades</span> are treated as percentages and are
                clamped on import to your degree’s minimum threshold (for example{" "}
                <span className="font-semibold">30</span> for many Bachelors rulesets or{" "}
                <span className="font-semibold">40</span> for many Masters rulesets) and a maximum of{" "}
                <span className="font-semibold">100</span>.
              </li>
              <li>
                <span className="font-semibold">Target grade</span> is used by{" "}
                <span className="font-semibold">Analyse</span> to estimate what you would need to
                achieve across remaining or unreleased modules.
              </li>
            </ul>
          </div>

          {/* Functionality */}
          <div className="rounded-lg border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-950">
            <h2 className="text-lg font-semibold">Functionality</h2>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-neutral-700 dark:text-neutral-200">
              <li>
                <span className="font-semibold">Calculate</span>: when required credits are complete,
                estimates your projected final grade and classification before official release.
              </li>
              <li>
                <span className="font-semibold">Analyse</span>{" "}
                <span className="text-xs">(Pro)</span>: when credits are incomplete, estimates the
                average needed to reach a chosen target grade or classification.
              </li>
              <li>
                <span className="font-semibold">Import / Export</span>{" "}
                <span className="text-xs">(Pro)</span>: save module inputs and analysis results for
                later reuse.
              </li>
              <li>
                <span className="font-semibold">Module Calculator</span>{" "}
                <span className="text-xs">(Pro)</span>: estimate or model individual module grades
                before or after results are released.
              </li>
            </ul>
          </div>

          {/* Coverage */}
          <div className="rounded-lg border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-950">
            <h2 className="text-lg font-semibold">Coverage</h2>
            <p className="mt-2 text-sm text-neutral-700 dark:text-neutral-200">
              Degree Classify UK supports over{" "}
              <span className="font-semibold">90</span> UK universities, applying institution-specific
              algorithms rather than a single generic calculation method.
            </p>
          </div>

          {/* Disclaimer */}
          <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3 text-xs text-neutral-600 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-400">
            This tool provides an educated estimate based on the ruleset and the data you enter.
            Results are not guaranteed and should not be treated as official. University regulations
            vary, can change, and may include discretionary, borderline, or regulatory adjustments.
            Always verify outcomes against your official course handbook and published university
            regulations.
          </div>
        </div>
      </Card>
    </div>
  );
}
